// REST client for the custom-style API (server/ in this repo; same contract
// as the former Map.Editor.Api at maps.vnpost.vn/styles/api).
// All endpoints require:
//   - Authorization: Bearer <Keycloak token>  (except the public style URL)
//   - X-Consumer-ID: <GUID>

import { getToken, getConsumerId, getActiveAccountId } from "./auth";
import { guardGlyphUrl } from "./glyph-guard";

// Every path the app calls lives under its own base (`/map-editor/`), so a
// gateway in front only has to forward that one prefix. The container's nginx
// (and the Vite dev proxy) route these sub-paths to the API service:
//   {base}styles/api/…        → API (editor calls, icon sprites, admin)
//   {base}maps/styles/…       → API public style links (older: {base}api/maps/custom/…)
//   {base}console/api/…       → Vietmap Console (see libs/auth.ts)
const APP_BASE = import.meta.env.BASE_URL;

// Override with VITE_MAP_EDITOR_API_URL to call another deployment directly.
const BASE_URL = (import.meta.env.VITE_MAP_EDITOR_API_URL as string | undefined)
  || `${APP_BASE}styles/api`;

// Absolute, copy-paste-able base URL of the API for links used outside this
// app (icon sprites). Defaults to this editor's own host; set
// VITE_MAP_EDITOR_PUBLIC_URL when the API is published elsewhere.
const PUBLIC_BASE_URL = (import.meta.env.VITE_MAP_EDITOR_PUBLIC_URL as string | undefined)
  || `${window.location.origin}${APP_BASE}styles/api`;

// Base of the public style links, shaped like Vietmap's own
// `https://maps.vietmap.vn/maps/styles/tm/style.json`; set
// VITE_MAP_EDITOR_PUBLIC_MAPS_URL when they are published elsewhere.
const PUBLIC_MAPS_URL = (import.meta.env.VITE_MAP_EDITOR_PUBLIC_MAPS_URL as string | undefined)
  || `${window.location.origin}${APP_BASE}maps`;

/**
 * ThemeMode is returned by the API as an integer enum:
 *   0 = tm (Street), 1 = lm (Light), 2 = dm (Dark)
 * (Confirmed against `GET /custom-styles` response payload.)
 */
export type ThemeMode = 0 | 1 | 2;
export const THEME_MODE = { tm: 0, lm: 1, dm: 2 } as const;

export type CustomStyleDto = {
  id: string;
  /** Globally unique id of the public link (new API). */
  publicId?: string;
  /** `{slug}_{publicId}` — see buildAbsoluteStyleUrl. */
  publicPath?: string;
  name: string;
  consumerId: string;
  themeMode: ThemeMode;
  description?: string;
  json: Record<string, unknown>;
  tags?: string[];
  inactive: boolean;
  /** An admin switched the public URL off (it answers 403). */
  publicDisabled?: boolean;
  /** Autosaved, unpublished work (unix s / author); GET one also has `draftJson`. */
  draftDt?: number;
  draftBy?: string;
  draftJson?: Record<string, unknown> | null;
  modifiedDt: number;
  modifiedBy: string;
  createdDt: number;
  createdBy: string;
};

type Result<T> = { code: string; message: string; data: T };
type ResultTotal<T> = { code: string; message: string; data: T[]; total: number; totalPage: number };

export class StylesApiError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

async function authHeaders(): Promise<HeadersInit> {
  const token = await getToken();
  if (!token) throw new StylesApiError("Not authenticated", "NO_TOKEN", 401);
  const consumerId = getConsumerId();
  if (!consumerId) {
    throw new StylesApiError(
      "Missing X-Consumer-ID. Set VITE_DEFAULT_CONSUMER_ID or ensure the Keycloak token carries a consumer_id claim.",
      "NO_CONSUMER",
      400
    );
  }
  const accountId = getActiveAccountId();
  return {
    Authorization: `Bearer ${token}`,
    "X-Consumer-ID": consumerId,
    // Lets the admin site show which Console account a style belongs to.
    ...(accountId ? { "X-Account-ID": accountId } : {}),
  };
}

async function parseResult<T>(res: Response): Promise<Result<T>> {
  const body = (await res.json()) as Result<T>;
  if (!res.ok || body.code !== "OK") {
    throw new StylesApiError(body.message || body.code || res.statusText, body.code || "HTTP_ERROR", res.status);
  }
  return body;
}

// ─── Create ───────────────────────────────────────────────────────────────────
export async function createCustomStyle(params: {
  name: string;
  themeMode: ThemeMode;
  description?: string;
  tags?: string[];
  json: Record<string, unknown>;
}): Promise<string> {
  const form = new FormData();
  form.append("Name", params.name);
  form.append("ThemeMode", String(params.themeMode));
  if (params.description) form.append("Description", params.description);
  // Send as multiple form fields with the same name — ASP.NET Core's default
  // model binder maps this to `Tags: List<string>`. We also append an empty
  // string when the list is empty so the backend (if it relies on the field's
  // presence to clear tags) can do so. Skip the empty entry on create —
  // creating with `Tags=` would just be noise.
  for (const t of params.tags || []) form.append("Tags", t);
  const file = new Blob([JSON.stringify(params.json)], { type: "application/json" });
  form.append("JsonFile", file, "style.json");

  const res = await fetch(`${BASE_URL}/custom-styles`, {
    method: "POST",
    headers: await authHeaders(),
    body: form,
  });
  const body = await parseResult<string>(res);
  return body.data;
}

// ─── Update ───────────────────────────────────────────────────────────────────
export async function updateCustomStyle(
  id: string,
  params: {
    name: string;
    themeMode: ThemeMode;
    description?: string;
    tags?: string[];
    json?: Record<string, unknown>;
  }
): Promise<boolean> {
  const form = new FormData();
  form.append("Name", params.name);
  form.append("ThemeMode", String(params.themeMode));
  if (params.description) form.append("Description", params.description);
  for (const t of params.tags || []) form.append("Tags", t);
  if (params.json) {
    const file = new Blob([JSON.stringify(params.json)], { type: "application/json" });
    form.append("JsonFile", file, "style.json");
  }

  const res = await fetch(`${BASE_URL}/custom-styles/${id}`, {
    method: "PUT",
    headers: await authHeaders(),
    body: form,
  });
  await parseResult<boolean>(res);
  return true;
}

// ─── Draft (autosave, not published) ──────────────────────────────────────────
export async function saveStyleDraft(id: string, json: Record<string, unknown>): Promise<number> {
  const res = await fetch(`${BASE_URL}/custom-styles/${id}/draft`, {
    method: "PUT",
    headers: { ...(await authHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({ json }),
  });
  return (await parseResult<{ draftDt: number }>(res)).data.draftDt;
}

export async function discardStyleDraft(id: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/custom-styles/${id}/draft`, { method: "DELETE", headers: await authHeaders() });
  await parseResult(res);
}

/** A style that has unpublished autosaved work newer than its last publish. */
export function hasNewerDraft(s: Pick<CustomStyleDto, "draftDt" | "modifiedDt">): boolean {
  return !!s.draftDt && s.draftDt > s.modifiedDt;
}

// ─── Duplicate ────────────────────────────────────────────────────────────────
export async function duplicateCustomStyle(id: string): Promise<{ id: string; name: string }> {
  const res = await fetch(`${BASE_URL}/custom-styles/${id}/duplicate`, { method: "POST", headers: await authHeaders() });
  return (await parseResult<{ id: string; name: string }>(res)).data;
}

/**
 * The style JSON as it is stored: no edit-tracking flags, no API key in the
 * metadata (the public URL injects the caller's).
 */
export function prepareStyleJson(style: Record<string, any>): Record<string, unknown> {
  const cleaned = stripTrackingMetadata({ ...style } as { layers?: any[] }) as Record<string, any>;
  const metadata = { ...(cleaned.metadata || {}) };
  delete metadata["maputnik:vietmap_access_token"];
  return { ...cleaned, metadata };
}

// ─── Read one ─────────────────────────────────────────────────────────────────
export async function getCustomStyle(id: string): Promise<CustomStyleDto> {
  const res = await fetch(`${BASE_URL}/custom-styles/${id}`, {
    method: "GET",
    headers: await authHeaders(),
  });
  const body = await parseResult<CustomStyleDto>(res);
  return body.data;
}

// ─── List ─────────────────────────────────────────────────────────────────────
export async function listCustomStyles(params: {
  page?: number;
  size?: number;
  keyword?: string;
  themeMode?: ThemeMode;
  tags?: string[];
} = {}): Promise<{ items: CustomStyleDto[]; total: number; totalPage: number }> {
  const qs = new URLSearchParams();
  qs.set("Page", String(params.page ?? 1));
  qs.set("Size", String(params.size ?? 20));
  if (params.keyword) qs.set("Keyword", params.keyword);
  if (params.themeMode !== undefined) qs.set("ThemeMode", String(params.themeMode));
  if (params.tags && params.tags.length) qs.set("Tags", params.tags.join(","));

  const res = await fetch(`${BASE_URL}/custom-styles?${qs.toString()}`, {
    method: "GET",
    headers: await authHeaders(),
  });
  const body = (await res.json()) as ResultTotal<CustomStyleDto>;
  if (!res.ok || body.code !== "OK") {
    throw new StylesApiError(body.message || body.code, body.code || "HTTP_ERROR", res.status);
  }
  return { items: body.data, total: body.total, totalPage: body.totalPage };
}

// ─── Delete (soft) ────────────────────────────────────────────────────────────
export async function deleteCustomStyle(id: string): Promise<boolean> {
  const res = await fetch(`${BASE_URL}/custom-styles/${id}`, {
    method: "DELETE",
    headers: await authHeaders(),
  });
  await parseResult<boolean>(res);
  return true;
}

// ─── Tags ─────────────────────────────────────────────────────────────────────
export async function listTags(params: {
  page?: number;
  size?: number;
  keyword?: string;
} = {}): Promise<{ items: string[]; total: number; totalPage: number }> {
  const qs = new URLSearchParams();
  qs.set("Page", String(params.page ?? 1));
  qs.set("Size", String(params.size ?? 50));
  if (params.keyword) qs.set("Keyword", params.keyword);

  const res = await fetch(`${BASE_URL}/custom-styles/tags?${qs.toString()}`, {
    method: "GET",
    headers: await authHeaders(),
  });
  const body = (await res.json()) as ResultTotal<string>;
  if (!res.ok || body.code !== "OK") {
    throw new StylesApiError(body.message || body.code, body.code || "HTTP_ERROR", res.status);
  }
  return { items: body.data, total: body.total, totalPage: body.totalPage };
}

// ─── Icon sprites (generated from recolourable icon sets) ─────────────────────
/**
 * Upload a sprite built in the browser (libs/icon-sprite-builder). The API
 * content-addresses it by set + version + config, so re-uploading the same
 * colours returns the same id. Returns the absolute sprite base URL for the
 * style's `sprite` field.
 */
export async function uploadIconSprite(params: {
  setId: string;
  setVersion: number;
  /** Canonical JSON of the colour config (see canonicalConfig). */
  config: string;
  sprite: { json: object; png: Blob };
  sprite2x: { json: object; png: Blob };
}): Promise<string> {
  const form = new FormData();
  form.append("SetId", params.setId);
  form.append("SetVersion", String(params.setVersion));
  form.append("Config", params.config);
  const json = (o: object) => new Blob([JSON.stringify(o)], { type: "application/json" });
  form.append("SpriteJson", json(params.sprite.json), "sprite.json");
  form.append("SpritePng", params.sprite.png, "sprite.png");
  form.append("Sprite2xJson", json(params.sprite2x.json), "sprite@2x.json");
  form.append("Sprite2xPng", params.sprite2x.png, "sprite@2x.png");
  const res = await fetch(`${BASE_URL}/icon-sprites`, { method: "POST", headers: await authHeaders(), body: form });
  const body = await parseResult<{ publicId: string; path: string }>(res);
  return `${PUBLIC_BASE_URL}/${body.data.path}`;
}

// ─── Iframe embed ─────────────────────────────────────────────────────────────
/**
 * Public embed page for a saved style (EmbedPage):
 *   {origin}{base}embed/{publicId}?apikey=…#zoom/lat/lng
 * Shows the published version (not drafts).
 */
export function buildEmbedUrl(
  style: Pick<CustomStyleDto, "publicId">,
  apiKey: string,
  view?: { zoom: number; center: { lat: number; lng: number } }
): string | null {
  if (!style.publicId) return null;
  const qs = apiKey ? `?apikey=${encodeURIComponent(apiKey)}` : "";
  const hash = view ? `#${view.zoom.toFixed(2)}/${view.center.lat.toFixed(5)}/${view.center.lng.toFixed(5)}` : "";
  return `${window.location.origin}${APP_BASE}embed/${style.publicId}${qs}${hash}`;
}

/** Copy-paste `<iframe>` for an embed URL. */
export function iframeCode(url: string, title = "Vietmap"): string {
  return `<iframe src="${url}" title="${title.replace(/"/g, "&quot;")}" width="100%" height="480" style="border:0" loading="lazy" allowfullscreen></iframe>`;
}

// ─── Label fonts (/fonts) ─────────────────────────────────────────────────────
// Google Fonts rendered to glyph PBFs in the browser (libs/glyph-builder) and
// stored by the API, which also redirects other fontstacks to Vietmap.

/** Style `glyphs` URL served by our API (keeps `{fontstack}`/`{range}` placeholders). */
export function labelGlyphsUrl(apiKey: string): string {
  return `${PUBLIC_BASE_URL}/fonts/{fontstack}/{range}.pbf${apiKey ? `?apikey=${encodeURIComponent(apiKey)}` : ""}`;
}

/** Version + built ranges of a hosted font; null when nobody built it yet. */
export async function getLabelFont(fontstack: string): Promise<{ version: number; ranges: number[] } | null> {
  const res = await fetch(`${BASE_URL}/fonts/${encodeURIComponent(fontstack)}`);
  if (res.status === 404) return null;
  return (await parseResult<{ version: number; ranges: number[] }>(res)).data;
}

export async function uploadLabelFont(params: {
  fontstack: string;
  family: string;
  version: number;
  ranges: Record<string, Blob>;
}): Promise<void> {
  const form = new FormData();
  form.append("Fontstack", params.fontstack);
  form.append("Family", params.family);
  form.append("Version", String(params.version));
  for (const [name, blob] of Object.entries(params.ranges)) form.append("Range", blob, name);
  const res = await fetch(`${BASE_URL}/fonts`, { method: "POST", headers: await authHeaders(), body: form });
  await parseResult(res);
}

// ─── Admin site (/admin/*) ────────────────────────────────────────────────────
// Needs the Console role SystemAdmins or AccountManagers in the token; no
// X-Consumer-ID — admins work across customers.

export type AdminStatus = "active" | "disabled" | "deleted" | "all";

export type AdminMe = {
  userId: string;
  username: string;
  roles: string[];
  role: "SystemAdmins" | "AccountManagers";
  isSystemAdmin: boolean;
  /** Accounts in scope (all of them for SystemAdmins). */
  accounts: { id: string; name: string; contactEmail?: string }[];
  /** Consumers an AccountManager reaches; null = unrestricted. */
  consumerCount: number | null;
};

export type AdminStyleDto = Omit<CustomStyleDto, "json"> & {
  json: Record<string, unknown> | null;
  publicDisabled: boolean;
  accountId: string | null;
  accountName: string | null;
  jsonBytes: number;
};

export type AdminAction =
  | "public_enable" | "public_disable" | "delete" | "restore" | "purge"
  | "rename" | "transfer" | "rotate_link" | "discard_draft";

export type AdminAuditEntry = {
  at: number;
  actorName: string;
  actorRole: string;
  action: AdminAction;
  styleId?: string | null;
  detail: Record<string, unknown>;
};

/** A row of the admin action log (GET /admin/audit). */
export type AdminAuditRow = AdminAuditEntry & {
  id: number;
  styleName: string | null;
  accountId: string | null;
  accountName: string | null;
  /** The style was purged since. */
  stylePurged: boolean;
};

/** Style counts + activity of an account / consumer / the whole scope. */
export type AdminStats = {
  active: number;
  disabled: number;
  deleted: number;
  /** Live styles with unpublished autosaved changes. */
  drafts: number;
  consumers: number;
  bytes: number;
  /** Styles changed (saved or drafted) in the last 7 days. */
  edits7d: number;
  lastDt: number | null;
  lastBy: string | null;
};

export type AdminAccountRow = { id: string; name: string | null; contactEmail: string | null; stats: AdminStats };

export type AdminAccounts = {
  accounts: AdminAccountRow[];
  /** Styles not tied to an account yet (saved without X-Account-ID). */
  unassigned: { consumerId: string; stats: AdminStats }[];
};

export type AdminAccountDetail = {
  account: { id: string; name: string | null; contactEmail: string | null };
  stats: AdminStats;
  consumers: { id: string; name: string | null; inConsole: boolean; stats: AdminStats }[];
  editors: { name: string; count: number; lastDt: number }[];
  recentStyles: AdminStyleDto[];
  audit: AdminAuditEntry[];
  sprites: number;
};

export type AdminOverview = {
  totals: AdminStats & { accountsWithStyles: number; accounts: number };
  activeAccounts: { id: string | null; name: string | null; stats: AdminStats }[];
  audit: AdminAuditEntry[];
};

type Owner = { accountId: string | null; accountName: string | null; consumerId: string; consumerName: string | null };

export type AdminLookupResult =
  | { kind: "style"; style: AdminStyleDto & { consumerName: string | null } }
  | ({ kind: "consumer"; stats: AdminStats } & Owner)
  | { kind: "account"; accountId: string; accountName: string | null; contactEmail: string | null; stats: AdminStats }
  | ({ kind: "sprite"; publicId: string; setId: string; createdBy: string; createdDt: number; usedBy: { id: string; name: string }[] } & Owner);

export type AdminBulkAction = "disable" | "enable" | "delete" | "restore";

export type AdminStyleList = {
  data: AdminStyleDto[];
  total: number;
  totalPage: number;
  counts: Record<AdminStatus, number>;
};

async function adminFetch<T>(path: string, init: RequestInit = {}): Promise<Result<T>> {
  const token = await getToken();
  if (!token) throw new StylesApiError("Not authenticated", "NO_TOKEN", 401);
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (init.body) headers["Content-Type"] = "application/json";
  const res = await fetch(`${BASE_URL}/admin${path}`, { ...init, headers });
  return parseResult<T>(res);
}

export async function getAdminMe(): Promise<AdminMe> {
  return (await adminFetch<AdminMe>("/me")).data;
}

export async function listAdminStyles(params: {
  page?: number;
  size?: number;
  keyword?: string;
  status?: AdminStatus;
  themeMode?: number | "";
  accountId?: string;
}): Promise<AdminStyleList> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") qs.set(k, String(v));
  const body = (await adminFetch<AdminStyleDto[]>(`/styles?${qs.toString()}`)) as Result<AdminStyleDto[]> &
    Omit<AdminStyleList, "data">;
  return { data: body.data, total: body.total, totalPage: body.totalPage, counts: body.counts };
}

export async function getAdminStyle(id: string): Promise<AdminStyleDto & { audit: AdminAuditEntry[] }> {
  return (await adminFetch<AdminStyleDto & { audit: AdminAuditEntry[] }>(`/styles/${encodeURIComponent(id)}`)).data;
}

export async function setAdminStylePublic(id: string, enabled: boolean): Promise<void> {
  await adminFetch(`/styles/${encodeURIComponent(id)}/public`, { method: "POST", body: JSON.stringify({ enabled }) });
}

export async function adminDeleteStyle(id: string): Promise<void> {
  await adminFetch(`/styles/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function adminRestoreStyle(id: string): Promise<void> {
  await adminFetch(`/styles/${encodeURIComponent(id)}/restore`, { method: "POST" });
}

export async function adminPurgeStyle(id: string): Promise<void> {
  await adminFetch(`/styles/${encodeURIComponent(id)}/permanent`, { method: "DELETE" });
}

export async function adminRenameStyle(id: string, change: { name?: string; description?: string }): Promise<void> {
  await adminFetch(`/styles/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(change) });
}

/** Move a style to another consumer (accountId: the consumer's account — needed by SystemAdmins). */
export async function adminTransferStyle(id: string, consumerId: string, accountId?: string): Promise<void> {
  await adminFetch(`/styles/${encodeURIComponent(id)}/transfer`, { method: "POST", body: JSON.stringify({ consumerId, accountId }) });
}

/** New public id — the old style URL / embed link stop working. */
export async function adminRotateStyleLink(id: string): Promise<string> {
  return (await adminFetch<{ publicId: string }>(`/styles/${encodeURIComponent(id)}/rotate-link`, { method: "POST" })).data.publicId;
}

export async function adminDiscardDraft(id: string): Promise<void> {
  await adminFetch(`/styles/${encodeURIComponent(id)}/draft`, { method: "DELETE" });
}

export async function adminBulkStyles(ids: string[], action: AdminBulkAction): Promise<{ done: number; skipped: { id: string; reason: string }[] }> {
  return (await adminFetch<{ done: number; skipped: { id: string; reason: string }[] }>("/styles/bulk", {
    method: "POST",
    body: JSON.stringify({ ids, action }),
  })).data;
}

/** Anything pasted — style id / URL / embed link, consumer, account, sprite URL — → owner. */
export async function adminLookup(q: string): Promise<AdminLookupResult[]> {
  return (await adminFetch<{ results: AdminLookupResult[] }>(`/lookup?q=${encodeURIComponent(q)}`)).data.results;
}

export async function getAdminOverview(): Promise<AdminOverview> {
  return (await adminFetch<AdminOverview>("/overview")).data;
}

export async function listAdminAccounts(): Promise<AdminAccounts> {
  return (await adminFetch<AdminAccounts>("/accounts")).data;
}

export async function getAdminAccount(id: string): Promise<AdminAccountDetail> {
  return (await adminFetch<AdminAccountDetail>(`/accounts/${encodeURIComponent(id)}`)).data;
}

/** Public links of every live style of the account off / on. */
export async function setAdminAccountPublic(id: string, enabled: boolean): Promise<number> {
  return (await adminFetch<{ changed: number }>(`/accounts/${encodeURIComponent(id)}/public`, {
    method: "POST",
    body: JSON.stringify({ enabled }),
  })).data.changed;
}

/** SystemAdmins: find the account of styles saved without one. */
export async function resolveAdminAccounts(): Promise<{ resolved: number; remaining: number }> {
  return (await adminFetch<{ resolved: number; remaining: number }>("/accounts/resolve", { method: "POST" })).data;
}

/** Every live style of an account as one JSON file (download). */
export async function exportAdminAccount(id: string): Promise<Blob> {
  const token = await getToken();
  if (!token) throw new StylesApiError("Not authenticated", "NO_TOKEN", 401);
  const res = await fetch(`${BASE_URL}/admin/accounts/${encodeURIComponent(id)}/export`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) await parseResult(res);
  return res.blob();
}

export async function listAdminAudit(params: {
  page?: number;
  size?: number;
  action?: string;
  actor?: string;
  accountId?: string;
  styleId?: string;
}): Promise<{ data: AdminAuditRow[]; total: number; totalPage: number }> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") qs.set(k, String(v));
  const body = (await adminFetch<AdminAuditRow[]>(`/audit?${qs.toString()}`)) as Result<AdminAuditRow[]> & { total: number; totalPage: number };
  return { data: body.data, total: body.total, totalPage: body.totalPage };
}

// ─── Public render URL builder ────────────────────────────────────────────────
/**
 * Absolute, standalone public URL for embedding outside this app:
 *   {PUBLIC_MAPS_URL}/styles/{publicId}/style.json?apikey=…
 * Returns the full style JSON with the key injected into tile / sprite / glyph
 * URLs; no auth header required. The id is globally unique and survives
 * renames. Falls back to the legacy `/edited-styles/{themeMode}/{name}` form
 * (the API redirects it) when the API didn't send a `publicId`.
 */
export function buildAbsoluteStyleUrl(
  style: Pick<CustomStyleDto, "themeMode" | "name" | "publicId">,
  apiKey: string,
  consumerId: string
): string {
  const qs = new URLSearchParams();
  qs.set("apikey", apiKey);
  if (style.publicId) {
    return `${PUBLIC_MAPS_URL}/styles/${style.publicId}/style.json?${qs.toString()}`;
  }
  qs.set("consumerId", consumerId);
  return `${PUBLIC_BASE_URL}/edited-styles/${style.themeMode}/${encodeURIComponent(style.name)}?${qs.toString()}`;
}

/**
 * Tracking metadata keys that live on a layer while the user is editing —
 * they MUST NOT leak into the saved style or the same layer would look
 * "edited" forever after a reload. Strip on save, strip again defensively on
 * load (in case an older save already polluted the JSON).
 */
const TRACKING_META_KEYS = [
  "maputnik:original_paint",
  "maputnik:user_hidden",
  "maputnik:edited_at",
] as const;

/**
 * Remove every edit-tracking flag from layer metadata in a style. Returns a
 * shallow-cloned style with cleaned layers — original input is untouched.
 * Layers whose metadata becomes empty get the field removed entirely so the
 * JSON stays clean.
 */
export function stripTrackingMetadata<T extends { layers?: any[] }>(style: T): T {
  if (!style || !Array.isArray(style.layers)) return style;
  const cleanedLayers = style.layers.map((l) => {
    const meta = l?.metadata;
    if (!meta || typeof meta !== "object") return l;
    let touched = false;
    const next: Record<string, any> = {};
    for (const [k, v] of Object.entries(meta)) {
      if ((TRACKING_META_KEYS as readonly string[]).includes(k)) {
        touched = true;
        continue;
      }
      next[k] = v;
    }
    if (!touched) return l;
    const out = { ...l };
    if (Object.keys(next).length === 0) delete out.metadata;
    else out.metadata = next;
    return out;
  });
  return { ...style, layers: cleanedLayers };
}

/**
 * The tiles/sources/glyphs/sprite URLs stored by the backend point at the
 * legacy `https://maps.vnpost.vn/stylemap` host. Rewrite them to the current
 * `https://maps.vnpost.vn/styles` host before handing the style to MapLibre.
 *
 * Applies to a single URL string. Safe to call on URLs that don't contain
 * the legacy prefix — it's a no-op in that case.
 */
export function rewriteLegacyStyleUrl(url: string): string {
  if (!url) return url;
  // Icon sprites saved before the API moved under the app base pointed at
  // `{origin}/styles/api/…`, which gateways may not forward any more.
  const oldApi = `${window.location.origin}/styles/api/`;
  if (APP_BASE !== "/" && url.startsWith(oldApi)) {
    url = `${window.location.origin}${APP_BASE}styles/api/${url.slice(oldApi.length)}`;
  }
  return guardGlyphUrl(url.replace("https://maps.vnpost.vn/stylemap", "https://maps.vnpost.vn/styles"));
}

/** Walk through a parsed MapLibre style and rewrite every external URL. */
export function rewriteLegacyStyleUrls(style: any): any {
  if (!style || typeof style !== "object") return style;
  if (style.glyphs && typeof style.glyphs === "string") {
    style.glyphs = rewriteLegacyStyleUrl(style.glyphs);
  }
  if (typeof style.sprite === "string") {
    style.sprite = rewriteLegacyStyleUrl(style.sprite);
  } else if (Array.isArray(style.sprite)) {
    style.sprite = style.sprite.map((s: { id: string; url: string }) => ({ ...s, url: rewriteLegacyStyleUrl(s.url) }));
  }
  if (style.sources && typeof style.sources === "object") {
    for (const src of Object.values(style.sources) as any[]) {
      if (!src || typeof src !== "object") continue;
      if (typeof src.url === "string") src.url = rewriteLegacyStyleUrl(src.url);
      if (Array.isArray(src.tiles)) {
        src.tiles = src.tiles.map((t: string) =>
          typeof t === "string" ? rewriteLegacyStyleUrl(t) : t
        );
      }
    }
  }
  return style;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
/**
 * Map a Vietmap preset style id (stored in metadata) to the API's ThemeMode.
 * Used to pre-fill the Save dialog.
 */
export function styleIdToThemeMode(styleId?: string): ThemeMode | null {
  switch (styleId) {
    case "vietmap-street": return THEME_MODE.tm;
    case "vietmap-light":  return THEME_MODE.lm;
    case "vietmap-dark":   return THEME_MODE.dm;
    default: return null;
  }
}
